import { BCDatePicker, BCDatePickerRef, BCMobileDateTimePicker } from '@bimser/components';
import { FormItemTypes, mobile } from '@bimser/core';
import dayjs from 'dayjs';
import 'dayjs/locale/az';
import 'dayjs/locale/de';
import 'dayjs/locale/ru';
import 'dayjs/locale/tr';
import 'dayjs/locale/zh';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';
import _isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { Portal } from 'react-portal';
import { IPropertyField, SizeType } from '../../../../entities/common';
import EditControl from '../../base/EditControl';
import { IPropertyChangedEventArgs } from '../../entities/EventArgs';
import Styles from './assets/style.scss';
import IDateTimePickerProperties from './entities/IDateTimePickerProperties';
import { IDisableDate } from './entities/IDisableDate';

dayjs.extend(localizedFormat);
dayjs.extend(localeData);
dayjs.extend(utc);

const pickerPopupId = 'datePickerPopupContainer';

export default class DateTimePicker extends EditControl<string> {
	format: string;
	allowClear: boolean;
	showTime: boolean;
	showToday: boolean;
	setTodayAsDefault: boolean;
	sizeType: SizeType;
	disableBefore?: IDisableDate;
	disableAfter?: IDisableDate;
	__todaySetFlag: boolean;
	inputRef = React.createRef<BCDatePickerRef>();

	getText(value: string = this.value) {
		const date = dayjs(value);

		if (!date.isValid()) {
			return '';
		}

		return date.format(this.format || dayjs.localeData().longDateFormat('L'));
	}

	get text() {
		return this.getText();
	}

	set text(value: string) {
		if (value) {
			const date = dayjs(value);

			if (date.isValid()) {
				this.__proxyChanging.value = date.format();
			}
		}
	}

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.DateTimePicker;
		this.disableDate = this.disableDate.bind(this);
		this.value = null;
		this.showTime = false;
		this.showToday = false;
		this.setTodayAsDefault = false;
		this.format = '';
		this.allowClear = true;
		this.disableAfter = {
			enabled: false,
			years: 0,
			months: 0,
			days: 0
		};
		this.disableBefore = {
			enabled: false,
			years: 0,
			months: 0,
			days: 0
		};

		this.__getControlByPlatform = this.__getControlByPlatform.bind(this);
		this.triggerValueChanged = this.triggerValueChanged.bind(this);

		this.subscribe('trigger:OnValueChanged', (value: string) => {
			if (_isEmpty(value)) {
				this.triggerValueChanged(null, undefined, undefined);
			} else {
				this.triggerValueChanged(dayjs(value), undefined, undefined);
			}
		});
	}

	__loadProperties(data: IDateTimePickerProperties) {
		super.__loadProperties(data);
		this.__proxyChanged.format = data.format;
		this.__proxyChanged.showTime = data.showTime;
		this.__proxyChanged.showToday = data.showToday;
		this.__proxyChanged.setTodayAsDefault = data.setTodayAsDefault;
		this.__proxyChanged.sizeType = data.sizeType;
		this.__proxyChanged.allowClear = data.allowClear;
		this.__proxyChanged.disableAfter = data.disableAfter;
		this.__proxyChanged.disableBefore = data.disableBefore;

		if (this.setTodayAsDefault && !this.value && !this.__todaySetFlag) {
			this.__proxyChanging.value = dayjs(
				dayjs(),
				this.format || dayjs().localeData().longDateFormat('L')
			).format();
			this.__todaySetFlag = true;
		}
	}

	__loadValueProperty(newValue: string) {
		const oldValue = this.value;
		this.__proxyChanged.value = newValue;

		if (this.__isInitialized) {
			const argument: IPropertyChangedEventArgs<string> = {
				name: this.name,
				newValue,
				oldValue
			};

			this.emit('event:OnValueChanged', argument);
			this.emit('event:server:OnValueChanged', {
				argument,
				controlId: this.name,
				name: 'ValueChanged',
				sender: 'web'
			});

			const newText = this.getText(newValue);
			const oldText = this.getText(oldValue);

			if (newText !== oldText && this.__isInitialized) {
				const argument: IPropertyChangedEventArgs<string> = {
					name: this.name,
					newValue: newText,
					oldValue: oldText
				};

				this.emit('event:OnTextChanged', argument);
				this.emit('event:server:OnTextChanged', {
					argument,
					controlId: this.name,
					name: 'TextChanged',
					sender: 'web'
				});
			}
		}
	}

	__loadTextProperty() {
		// override
	}

	protected triggerValueChanged(date: dayjs.Dayjs, isoZoneStrings: string, dateString: string) {
		this.__proxyChanging.value = date ? date.format() : null;
	}

	focus() {
		this.inputRef.current.focus();
	}

	__getPropertyFields(properties?: Map<string, any>): IPropertyField[] {
		const fields = super.__getPropertyFields();

		const format = properties.get('format');
		const showTime = properties.get('showTime');
		const showToday = properties.get('showToday');

		return [
			...fields,
			{
				name: 'value',
				type: 'date',
				options: {
					format,
					showTime,
					showToday
				}
			},
			{
				name: 'format',
				type: 'string'
			},
			{
				name: 'showTime',
				type: 'boolean'
			},
			{
				name: 'showToday',
				type: 'boolean'
			},
			{
				name: 'setTodayAsDefault',
				type: 'boolean'
			},
			{
				name: 'disableBefore',
				type: 'object'
			},
			{
				name: 'disableAfter',
				type: 'object'
			}
		];
	}

	__getComponentData() {
		const format = this.format || dayjs().localeData().longDateFormat('L');
		return {
			...super.__getComponentData(),
			format: format,
			showTime: this.showTime,
			showToday: this.showToday,
			disabled: this.__isDisabled(),
			value: this.value ? dayjs(new Date(this.value)).format(format) : null,
			style: { width: '100%', ...this.style },
			allowClear: this.allowClear && !this.readOnly,
			setTodayAsDefault: this.setTodayAsDefault,
			sizeType: this.sizeType,
			disableAfter: this.disableAfter,
			disableBefore: this.disableBefore
		};
	}

	__getSizeType() {
		return this.sizeType ?? this.__form.sizeType;
	}

	disableDate(current: dayjs.Dayjs) {
		const { disableBefore, disableAfter } = this;

		if (disableBefore?.enabled) {
			const beforeDate = dayjs(
				`${disableBefore.years}-${disableBefore.months}-${disableBefore.days}`,
				'YYYY-M-D'
			);
			if (current < beforeDate) {
				return true;
			}
		}

		if (disableAfter?.enabled) {
			const afterDate = dayjs(
				`${disableAfter.years}-${disableAfter.months}-${disableAfter.days}`,
				'YYYY-M-D'
			);
			if (current >= afterDate) {
				return true;
			}
		}

		return false;
	}

	getDisableBefore(): Date {
		if (this.disableBefore.enabled) {
			return new Date(
				this.disableBefore.years,
				this.disableBefore.months - 1,
				this.disableBefore.days
			);
		}
		return undefined;
	}

	getDisableAfter(): Date {
		if (this.disableAfter.enabled) {
			return new Date(
				this.disableAfter.years,
				this.disableAfter.months - 1,
				this.disableAfter.days - 1
			);
		}
		return undefined;
	}

	__renderPopupContainer() {
		if (!this.__form.getShadowDomMode()) {
			return (
				<Portal>
					<div id={pickerPopupId}></div>
				</Portal>
			);
		}
	}

	__getControlByPlatform() {
		const format = this.format || dayjs().localeData().longDateFormat('L');

		if (mobile().any()) {
			return (
				<BCMobileDateTimePicker
					format={format}
					showTime={this.showTime}
					disabled={this.__isDisabled()}
					value={this.value ? dayjs(new Date(this.value)) : undefined}
					readOnly={this.__isReadOnly()}
					onChange={this.triggerValueChanged}
					disableBefore={this.getDisableBefore()}
					disableAfter={this.getDisableAfter()}
					placeholder={this.placeholder.getText()}
					style={{ width: '100%', ...this.style }}
					textAlign={this.textAlign}
					allowClear={this.__isReadOnly() ? false : this.allowClear}
				/>
			);
		}

		return (
			<>
				{this.__renderPopupContainer()}
				<BCDatePicker
					ref={this.inputRef}
					tabIndex={this.tabIndex}
					disabledDate={this.disableDate}
					format={format}
					showTime={this.showTime}
					showToday={this.showToday}
					disabled={this.__isDisabled()}
					placeholder={this.placeholder.getText()}
					value={this.value ? dayjs(new Date(this.value)) : undefined}
					style={{ width: '100%', ...this.style }}
					onChange={this.triggerValueChanged}
					allowClear={this.__isReadOnly() ? false : this.allowClear}
					className={Styles.transparentContent}
					size={this.__getSizeType()}
					readOnly={this.__isReadOnly()}
					textAlign={this.textAlign}
					getPopupContainer={() => this.__form.__getShadowPopupContainer(pickerPopupId)}
					extractPopupKey={this.__form.getShadowDomMode() && pickerPopupId}
				/>
			</>
		);
	}

	__render(children?: React.ReactNode) {
		return super.__render(this.__getControlByPlatform());
	}
}
